/**
 * PERCHÉ UN RECORD È ESCLUSO — le condizioni cadute, gruppo per gruppo.
 *
 * Estratto da `AutomationSimulation.tsx`, dove è nato: la stessa domanda se la pone il MONITORAGGIO
 * sui record che il motore ha scartato davvero (stato `skipped`), e la risposta deve essere la
 * stessa frase. Due copie della stessa spiegazione, una per schermata, diventerebbero due
 * spiegazioni diverse della stessa regola alla prima modifica.
 *
 * Il modulo non conosce nessuna delle due schermate: prende un esito, la configurazione
 * dell'automazione e il catalogo dei campi, e rende righe da incolonnare.
 */

import type { TFunction } from 'i18next';
import { summarizeConditionParts, SUMMARY_PLACEHOLDER } from './automationSummary';
import type {
  AutomationConditionGroup,
  AutomationSimulationCondition,
  CatalogField,
} from './automationTypes';

/**
 * Perché un gruppo è caduto: il suo numero e le condizioni che il record non ha soddisfatto, una
 * per elemento invece che in una frase sola.
 *
 * Sono separate e non concatenate perché a video vanno **incolonnate**: un gruppo con quattro
 * condizioni cadute produrrebbe altrimenti una riga larghissima in una tabella che ha già una
 * colonna per campo. Su una tabella l'altezza costa meno della larghezza.
 *
 * Ogni pezzo porta l'id della propria condizione, che è la sua chiave di rendering: il testo non lo
 * è, perché due condizioni possono produrne uno identico — stesso campo, stesso operatore, valori
 * diversi che il riepilogo abbrevia — e il numero di gruppo nemmeno, visto che qui i fratelli sono
 * dello stesso gruppo.
 */
export interface ExclusionReason {
  group: number;
  conditions: {
    automationConditionId: number;
    /** Quel che si confronta, o la causa tecnica quando la condizione non era valutabile. */
    text: string;
    /** Il valore atteso, che si rende in corsivo. Assente sulle condizioni non valutabili, dove un
     *  confronto non c'è: la loro causa è tutta nel testo. */
    value?: string;
  }[];
}

/**
 * Perché un record è escluso: le condizioni cadute, gruppo per gruppo.
 *
 * I gruppi sono in OR, quindi un record escluso li ha fatti cadere TUTTI, e citarne uno solo
 * racconterebbe un ramo su N. Dentro il gruppo si mostrano TUTTE le condizioni cadute, non la
 * prima: sono in AND, quindi per rendere idoneo quel record bisognerebbe soddisfarle tutte quante.
 * Una sola sarebbe una risposta vera alla domanda "perché è caduto il gruppo" — ne basta una — ma
 * farebbe sembrare il lavoro più piccolo di quello che è a chi legge per correggere l'automazione.
 *
 * In ingresso ci sono gli ESITI e non un record: la funzione ne usava solo `conditions`, e
 * prenderli sciolti la rende indipendente da chi glieli porta — la simulazione manda tutte le
 * condizioni di ogni record, il monitoraggio le sole cadute di un'esecuzione.
 *
 * Il livello gruppo non viaggia nella risposta — le condizioni sono in lista piatta — e si
 * ricostruisce qui dalla configurazione dell'automazione, che la pagina ha già in store: è la
 * ragione per cui il DTO porta `automationConditionId` invece di replicare la regola per ogni riga.
 *
 * La forma è TECNICA di proposito: etichetta del campo e CODICE dell'operatore, non una frase.
 * Comporre "Importo maggiore o uguale a 10.000" richiederebbe il catalogo condizioni per risolvere
 * simbolo e valore configurato — una chiamata in più e la stessa macchina del riepilogo — mentre
 * qui basta dire QUALE confronto è caduto: il valore che l'ha fatto cadere è già nella colonna
 * corrispondente della stessa riga, e la soglia sta nell'automazione, a un clic.
 */
export function exclusionReasons(
  outcomes: AutomationSimulationCondition[],
  groups: AutomationConditionGroup[],
  conditionFields: CatalogField[],
  t: TFunction,
): ExclusionReason[] {
  // Catalogo non ancora arrivato: nessun motivo, invece di un elenco di segnaposto. Le condizioni
  // non valutabili se la caverebbero anche senza — la loro causa è una chiave, non un confronto —
  // ma una lista metà scritta e metà puntini si legge peggio di una lista assente, e il catalogo
  // arriva comunque in una manciata di millisecondi.
  if (conditionFields.length === 0) return [];

  return groups.flatMap((group, index) => {
    // Le condizioni cadute del gruppo, ciascuna con accanto la propria configurazione: servono
    // entrambe — l'esito porta la causa, la configurazione campo, operatore e valore — e si
    // accoppiano in una passata sola. Si scorre nell'ordine del GRUPPO, che è quello in cui
    // l'utente le ha composte.
    const fallen = group.conditions
      .map((configured) => ({
        configured,
        outcome: outcomes.find(
          (c) => c.automationConditionId === configured.automationConditionId,
        ),
      }))
      .filter(({ outcome }) => outcome && !outcome.satisfied);

    if (fallen.length === 0) return [];

    // Due modi di raccontare, per due cose diverse.
    // NON valutabile: porta la propria causa e non un confronto da mostrare. La chiave la traduce
    // il client, con un fallback per quelle che questo frontend non conosce ancora — il backend può
    // aggiungerne senza che la tabella smetta di dire qualcosa di sensato.
    // Valutata e non soddisfatta: si racconta con la regola stessa, e a comporla è la funzione del
    // riepilogo del builder — stessa frase per la stessa condizione nelle due schermate, e nessuna
    // etichetta di operatore duplicata qui dentro.
    return [
      {
        group: index + 1,
        conditions: fallen.map(({ configured, outcome }) => {
          if (outcome?.reasonKey)
            return {
              automationConditionId: configured.automationConditionId,
              text: t(`automation.conditions.reasons.${outcome.reasonKey}`, {
                defaultValue: t('automation.conditions.reasons.unknown'),
              }),
            };

          // Condizione che il catalogo non sa nominare: resta il segnaposto, senza valore da
          // staccare in corsivo. Non dovrebbe capitare — un campo sparito dal provider rende la
          // condizione non valutabile, quindi cade nel ramo sopra — ma il ripiego lo sceglie chi
          // rende, ed è questo.
          const parts = summarizeConditionParts(configured, conditionFields, t);

          return {
            automationConditionId: configured.automationConditionId,
            text: parts?.subject ?? SUMMARY_PLACEHOLDER,
            value: parts?.value,
          };
        }),
      },
    ];
  });
}
